import React, { useEffect, useId, useRef, useState } from "react";
import { GlobalButton, GlobalTextField } from "./GlobalFormFields";
import "./GlobalUserSearchField.css";

/**
 * Searchable staff/user picker (typeahead) — same UX as receptionist CreateCallLogForm.
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} [props.required]
 * @param {string|number} [props.value] — selected user id
 * @param {(id: string|number) => void} props.onChange
 * @param {(users: object[]) => Promise<{ data?: object[] }>} props.searchUsers — API fn
 * @param {"form"|"filter"} [props.variant] — form: card when selected; filter: compact, no card
 * @param {string} [props.error]
 * @param {string} [props.placeholder]
 * @param {string} [props.initialDisplayName] — when editing, show until user changes
 * @param {string} [props.initialDisplayDepartment] — role/department label for edit mode
 * @param {string} [props.wrapClassName] — e.g. gus-wrap--form-span
 * @param {string} [props.helperText] — shown when no user is selected yet
 */
export default function GlobalUserSearchField({
  label = "Handled By",
  required = false,
  value,
  onChange,
  searchUsers,
  variant = "form",
  error,
  placeholder = "Search name...",
  initialDisplayName = "",
  initialDisplayDepartment = "",
  wrapClassName = "",
  minQueryLength = 2,
  helperText = "",
}) {
  const deptLabel = (user) => {
    const raw = user?.department_name ?? user?.role_name;
    return raw != null ? String(raw).trim() : "";
  };
  const baseId = useId();
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedLabel, setSelectedLabel] = useState(initialDisplayName || "");
  const [selectedDepartment, setSelectedDepartment] = useState(
    initialDisplayDepartment || "",
  );
  const wrapRef = useRef(null);

  const normalizeResults = (resp) => {
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.data)) return resp.data;
    return [];
  };

  useEffect(() => {
    if (initialDisplayName) setSelectedLabel(initialDisplayName);
    if (initialDisplayDepartment) setSelectedDepartment(initialDisplayDepartment);
  }, [initialDisplayName, initialDisplayDepartment]);

  useEffect(() => {
    if (!value) {
      setUserSearch("");
      setSelectedLabel("");
      setSelectedDepartment("");
      setUserResults([]);
      setShowResults(false);
    }
  }, [value]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!userSearch || userSearch.length < minQueryLength) {
        setUserResults([]);
        setShowResults(false);
        return;
      }
      setSearching(true);
      setSearchError("");
      try {
        const resp = await searchUsers(userSearch);
        const list = normalizeResults(resp);
        setUserResults(list);
        setShowResults(true);
      } catch (err) {
        setUserResults([]);
        setShowResults(true);
        setSearchError(
          err?.message || "Unable to load staff list. Sign in again or refresh.",
        );
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch, searchUsers, minQueryLength]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleSelect = (u) => {
    onChange?.(u.id);
    const name = u.name || "Unknown User";
    setSelectedLabel(name);
    setSelectedDepartment(deptLabel(u));
    if (variant === "filter") {
      setUserSearch(name);
    } else {
      setUserSearch("");
    }
    setShowResults(false);
    setUserResults([]);
  };

  const handleClear = () => {
    onChange?.("");
    setUserSearch("");
    setSelectedLabel("");
    setSelectedDepartment("");
    setUserResults([]);
    setShowResults(false);
  };

  const showCard = variant === "form" && !!value;
  const hasSelection = value !== "" && value != null;

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter" || !showResults || userResults.length === 0) return;
    e.preventDefault();
    handleSelect(userResults[0]);
  };

  useEffect(() => {
    if (variant !== "filter" || !value || selectedLabel) return;
    setUserSearch((prev) => (prev ? prev : `User #${value}`));
  }, [variant, value, selectedLabel]);

  return (
    <div
      ref={wrapRef}
      className={`gus-wrap ${wrapClassName}`.trim()}
      id={`gus-${baseId}`}
    >
      <label className="gff-label" htmlFor={`gus-input-${baseId}`}>
        {label}
        {required ? <span style={{ color: "#dc2626" }}> *</span> : null}
      </label>

      {showCard ? (
        <div className="gus-selected-card">
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div className="gus-selected-card__avatar">
              {(selectedLabel || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                {selectedLabel}
              </div>
              {selectedDepartment ? (
                <div className="gus-selected-card__meta">{selectedDepartment}</div>
              ) : null}
            </div>
          </div>
          <GlobalButton type="button" variant="ghost" size="sm" onClick={handleClear}>
            Change
          </GlobalButton>
        </div>
      ) : (
        <>
          <GlobalTextField
            id={`gus-input-${baseId}`}
            label="Search users"
            value={userSearch}
            onChange={(v) => {
              setUserSearch(v);
              if (variant === "filter" && value) {
                onChange?.("");
                setSelectedLabel("");
              }
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder={placeholder}
            error={hasSelection ? error : undefined}
            classNames={{
              input:
                !hasSelection && error
                  ? "gff-input gus-input--error"
                  : "gff-input",
              fieldWrap: "gff-field",
              label: "gff-label gus-inner-label-sr",
            }}
          />
          {!hasSelection && helperText ? (
            <p className="gus-helper">{helperText}</p>
          ) : null}
          {searching ? <span className="gus-searching">Searching...</span> : null}

          {showResults && userResults.length > 0 ? (
            <div className="gus-dropdown" role="listbox">
              {userResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="gus-row"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(u)}
                >
                  <div>
                    <div className="gus-row__name">{u.name || "Unknown User"}</div>
                    {deptLabel(u) ? (
                      <div className="gus-row__meta">{deptLabel(u)}</div>
                    ) : null}
                  </div>
                  <span className="gus-row__action">Select</span>
                </button>
              ))}
            </div>
          ) : null}

          {showResults &&
            userResults.length === 0 &&
            !searching &&
            userSearch.length >= minQueryLength ? (
            <div className="gus-dropdown" role="listbox">
              <div className="gus-empty">
                {searchError || "No users found — try another name"}
              </div>
            </div>
          ) : null}
        </>
      )}

      {variant === "filter" && value ? (
        <div style={{ marginTop: "0.35rem" }}>
          <GlobalButton type="button" variant="ghost" size="sm" onClick={handleClear}>
            Clear selection
          </GlobalButton>
        </div>
      ) : null}

      {!hasSelection && error ? (
        <p className="gfb-feedback gfb-feedback--error gus-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
